import * as React from 'react'
import { useTranslation } from '@clofo/i18n'
import { IceIcon } from './ice-icon'
import { Button } from './button'
import { cn } from '../utils'

function Input({
  className,
  type,
  clearInput,
  clearLabel,
  ...props
}: React.ComponentProps<'input'> & {
  clearInput?: () => void
  /**
   * Accessible name for the clear button, for a field where a bare "Clear" would not say what
   * is being cleared. Defaults to "Clear".
   */
  clearLabel?: string
}) {
  const hasValue = typeof props.value === 'string' ? props?.value?.length > 0 : false
  return (
    <div className='relative w-full'>
      <input
        type={type}
        data-slot='input'
        className={cn(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-sm transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:border-primary focus-visible:shadow-xs',
          'aria-invalid:border-destructive',
          className,
          clearInput && 'pr-10',
        )}
        {...props}
      />
      {clearInput && hasValue && <ClearInputButton label={clearLabel} onClear={clearInput} />}
    </div>
  )
}

/**
 * Split out so the translation hook runs only for the handful of inputs that offer a clear
 * button, rather than on every `Input` in the app.
 */
function ClearInputButton({ label, onClear }: { label: string | undefined; onClear: () => void }) {
  const { t } = useTranslation()

  return (
    <Button
      type='button'
      aria-label={label ?? t('common.clear')}
      variant='icon'
      size='icon'
      className='absolute top-0 right-1'
      onMouseDown={event => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault()
        event.stopPropagation()
        onClear()
      }}
    >
      <IceIcon name='close-delete-x' />
    </Button>
  )
}

export { Input }
